import React, { useEffect, useRef, useCallback, useState } from "react";
import { Modal, Button, Progress } from "antd";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getBrandedLoginPath } from "@/pages/signup/brandContext";

interface IdleTimeoutMonitorProps {
  children: React.ReactNode;
  // idle timeout time (milliseconds), default to 30 minutes
  // idleTimeout value should be greater than warningTime
  idleTimeout: number;
  // warning time (milliseconds), default to 2 minutes before timeout
  // warningTime value should be less than idleTimeout
  warningTime: number;
  // whether to enable multi-tab synchronization
  enableMultiTabSync?: boolean;
}

const IdleTimeoutMonitor: React.FC<IdleTimeoutMonitorProps> = ({
  children,
  idleTimeout,
  warningTime,
  enableMultiTabSync = true,
}) => {
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const lastActivityTimeRef = useRef<number>(Date.now());
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const countdownIntervalRef = useRef<ReturnType<typeof setTimeout>>();

  const isShowingWarningRef = useRef<boolean>(false);
  const handleIdleTimeoutRef = useRef<(() => Promise<void>) | null>(null);

  // component-level ref to prevent concurrent logout executions
  const isLoggingOutRef = useRef<boolean>(false);

  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const LAST_ACTIVITY_KEY = "user_last_activity_time";
  const SYNC_ACTIVITY_KEY = "user_activity_sync";

  /**
   * last activity time
   */
  const updateLastActivityTime = useCallback(() => {
    const now = Date.now();
    lastActivityTimeRef.current = now;

    // sync to localStorage for other tabs
    if (enableMultiTabSync) {
      localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
      // trigger storage event to notify other tabs
      localStorage.setItem(SYNC_ACTIVITY_KEY, now.toString());
    }
  }, [enableMultiTabSync]);

  /**
   * reset all timers
   */
  const resetTimers = useCallback(() => {
    // clear existing timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // close warning dialog (update state and ref)
    setShowWarning(false);
    isShowingWarningRef.current = false;

    // update last activity time
    updateLastActivityTime();

    // set warning timer
    const warningDelay = idleTimeout - warningTime;
    warningTimerRef.current = setTimeout(() => {
      // immediately set ref to prevent any event handling
      isShowingWarningRef.current = true;
      setShowWarning(true);

      // calculate absolute deadline timestamp to prevent browser throttling issues
      // this ensures correct timeout even if the tab is in background
      const deadline = Date.now() + warningTime;
      setRemainingSeconds(Math.ceil(warningTime / 1000));

      // clear idle timer to prevent race condition
      // from this point, only the countdown interval (via useEffect) will trigger logout
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = undefined;
      }

      // start countdown using absolute time comparison
      // this prevents issues with browser throttling in background tabs
      countdownIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const diff = Math.ceil((deadline - now) / 1000);

        if (diff <= 0) {
          setRemainingSeconds(0);
          // clear interval, let useEffect trigger logout
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
        } else {
          setRemainingSeconds(diff);
        }
      }, 1000);
    }, warningDelay);

    // set idle timeout timer (will be cleared when warning appears)
    idleTimerRef.current = setTimeout(() => {
      // fallback: this should not execute if warning was shown
      // kept as safety net in case warning timer fails
      handleIdleTimeoutRef.current?.();
    }, idleTimeout);
  }, [idleTimeout, warningTime, updateLastActivityTime]);

  /**
   * handle idle timeout
   */
  const handleIdleTimeout = useCallback(async () => {
    // prevent concurrent executions from multiple invocations
    if (isLoggingOutRef.current) {
      return;
    }
    isLoggingOutRef.current = true;

    // immediately set ref to prevent any subsequent event resetting timers
    isShowingWarningRef.current = false;

    // close warning dialog
    setShowWarning(false);

    const executeLogout = async () => {
      try {
        // destroy all modals before logout to prevent showing on login page
        Modal.destroyAll();
        await logout();
      } catch (error) {
        console.error("Logout error:", error);
      } finally {
        navigate(getBrandedLoginPath(), { replace: true });
      }
    };

    // store timeout ID to allow cancellation
    let autoLogoutTimeoutId: NodeJS.Timeout | null = null;

    // show timeout warning message
    Modal.warning({
      title: "Session has expired",
      content:
        "You have been automatically logged out due to inactivity. You will be redirected to the login page in 2 seconds...",
      okText: "Return to login",
      closable: false,
      maskClosable: false,
      onOk: async () => {
        // cancel the auto-logout timeout when user clicks the button
        if (autoLogoutTimeoutId) {
          clearTimeout(autoLogoutTimeoutId);
        }
        await executeLogout();
      },
    });

    // if user doesn't click, logout and redirect to login page after 2 seconds
    autoLogoutTimeoutId = setTimeout(async () => {
      await executeLogout();
    }, 2000);
  }, [logout, navigate]);

  // update ref whenever handleIdleTimeout changes
  useEffect(() => {
    handleIdleTimeoutRef.current = handleIdleTimeout;
  }, [handleIdleTimeout]);

  /**
   * user chooses to continue session
   */
  const handleContinueSession = useCallback(() => {
    // reset warning state
    isShowingWarningRef.current = false;
    resetTimers();
  }, [resetTimers]);

  /**
   * user activity event handler (throttling)
   */
  const handleUserActivity = useCallback(
    (_event?: Event) => {
      // 🔴 critical fix: use ref immediately to check, not dependent on state update
      if (isShowingWarningRef.current) {
        return;
      }

      // only handle if user is authenticated
      if (!isAuthenticated) {
        return;
      }

      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityTimeRef.current;

      // throttling: if the time since last activity is less than 5 seconds, do not handle
      if (timeSinceLastActivity < 5000) {
        return;
      }

      resetTimers();
    },
    [isAuthenticated, resetTimers],
  );

  /**
   * listen to other tabs' activity (through localStorage)
   */
  useEffect(() => {
    if (!enableMultiTabSync || !isAuthenticated) {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SYNC_ACTIVITY_KEY && e.newValue) {
        const otherTabActivityTime = parseInt(e.newValue, 10);
        const currentActivityTime = lastActivityTimeRef.current;

        // if other tabs have updated activity time, sync and reset timers
        if (otherTabActivityTime > currentActivityTime) {
          // 🔴 use ref instead of state
          if (isShowingWarningRef.current) {
            return;
          }
          resetTimers();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [enableMultiTabSync, isAuthenticated, resetTimers]);

  /**
   * initialize and cleanup
   */
  useEffect(() => {
    if (!isAuthenticated) {
      // user is not logged in, clear all timers
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
      setShowWarning(false);
      isShowingWarningRef.current = false;
      return;
    }

    // user is logged in, start timers
    resetTimers();

    // listen to user activity events
    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((eventName) => {
      document.addEventListener(
        eventName,
        handleUserActivity as EventListener,
        {
          passive: true,
        },
      );
    });

    // page visibility change
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        // 🔴 if warning dialog is showing, do not handle visibility change
        if (isShowingWarningRef.current) {
          return;
        }

        // when page becomes visible, check if timers need to be reset
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityTimeRef.current;

        if (timeSinceLastActivity < idleTimeout) {
          // still within the valid period, reset timers
          resetTimers();
        } else if (timeSinceLastActivity >= idleTimeout) {
          // already timed out, trigger logout
          handleIdleTimeoutRef.current?.();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // cleanup function
    return () => {
      events.forEach((eventName) => {
        document.removeEventListener(
          eventName,
          handleUserActivity as EventListener,
        );
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
    };
  }, [isAuthenticated, idleTimeout, resetTimers, handleUserActivity]);

  /**
   * when countdown reaches zero, automatically logout
   * This is the primary logout trigger after warning is shown
   * (idle timer is cleared when warning appears to prevent race condition)
   */
  useEffect(() => {
    if (remainingSeconds === 0 && showWarning) {
      handleIdleTimeoutRef.current?.();
    }
  }, [remainingSeconds, showWarning]);

  // calculate progress percentage
  const progressPercent = Math.floor(
    (remainingSeconds / (warningTime / 1000)) * 100,
  );

  return (
    <>
      {children}

      <Modal
        open={showWarning}
        title="Session is about to expire"
        closable={false}
        maskClosable={false}
        footer={[
          <Button key="logout" onClick={handleIdleTimeout}>
            Logout
          </Button>,
          <Button key="continue" type="primary" onClick={handleContinueSession}>
            Continue
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <p style={{ fontSize: "16px", marginBottom: "20px" }}>
            Your session is about to expire due to inactivity.
          </p>
          <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
            To stay logged in, please click "Continue".
          </p>
          <div style={{ marginBottom: "10px" }}>
            <span
              style={{ fontSize: "32px", fontWeight: "bold", color: "#ff4d4f" }}
            >
              {remainingSeconds}
            </span>
            <span style={{ fontSize: "16px", marginLeft: "8px" }}>
              seconds until automatic logout
            </span>
          </div>
          <Progress
            percent={progressPercent}
            status={remainingSeconds < 30 ? "exception" : "active"}
            showInfo={false}
          />
        </div>
      </Modal>
    </>
  );
};

export default IdleTimeoutMonitor;
